import React__default, { useRef, useCallback } from 'react';
import { useEventListener } from '@shopify/polaris';

/*
  Executes a given callback when a click occurs outside of this component

  How this works:
    First, we check whether the event target is inside of this component in the DOM. If it's not, we use the following
    logic to compare synthetic events to native events.

    When a synthetic events triggers inside of this component, we save a reference to event.nativeEvent.
    When a native event triggers at the document level, we compare this new event reference to the saved event.

    If the references are the same, we know that the event was triggered from inside of this component event if the
    document listener triggered first. This works around the fact that native listeners are not aware of React portals,
    so event bubbling doesn't work as usual.
*/
function OutsideClickDetection({
  callback,
  children
}) {
  var _insideWrapperNode$cu2;
  const insideWrapperNode = useRef(null);
  const latestInsideEventRef = useRef(null);
  const memoizedCallback = useCallback(outsideEvent => {
    // Since we're listening at the capture phase, the document listener will always be invoked before children
    // listeners. setTimeout allows us to wait until the capture phase finishes and both handlers have been invoked.
    setTimeout(() => {
      var _insideWrapperNode$cu;
      const latestInsideEvent = latestInsideEventRef.current;
      latestInsideEventRef.current = null;
      if ((_insideWrapperNode$cu = insideWrapperNode.current) !== null && _insideWrapperNode$cu !== void 0 && _insideWrapperNode$cu.contains(outsideEvent.target) || latestInsideEvent === outsideEvent) {
        // Inside click
        return;
      }
      return callback(outsideEvent);
    });
  }, [callback]);
  useEventListener('click', memoizedCallback, (_insideWrapperNode$cu2 = insideWrapperNode.current) === null || _insideWrapperNode$cu2 === void 0 ? void 0 : _insideWrapperNode$cu2.ownerDocument, {
    capture: true
  });

  // It's important to register the listener at the capture phase, because inside click events may
  // be stopped from bubbling up with `event.stopPropagation()` at the target/bubbling phase
  function handleInsideClickCapture(event) {
    latestInsideEventRef.current = event.nativeEvent;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ref: insideWrapperNode,
    onClickCapture: handleInsideClickCapture
  }, children);
}

export { OutsideClickDetection };
